import { useEffect, useRef, useState } from 'react';

const counters = [
  { icon: 'ri-eye-line', end: 128470, label: 'Total Views', suffix: '+' },
  { icon: 'ri-user-line', end: 8392, label: 'Active Players', suffix: '+' },
  { icon: 'ri-treasure-map-line', end: 50, label: 'Secrets Unlocked', suffix: '+' },
  { icon: 'ri-star-line', end: 4.9, label: 'Average Rating', suffix: '/5', decimals: 1 },
];

function useCountUp(end: number, duration = 2000, decimals = 0) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let startTime: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(parseFloat((eased * end).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, end, duration, decimals]);

  return { count, ref };
}

function CounterItem({ icon, end, label, suffix, decimals = 0 }: { icon: string; end: number; label: string; suffix: string; decimals?: number }) {
  const { count, ref } = useCountUp(end, 2200, decimals);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center gap-2 px-4 py-6 rounded-2xl border border-purple-800/40 transition-all duration-300 hover:border-purple-500/60 hover:scale-105"
      style={{ background: 'rgba(168,85,247,0.07)' }}
    >
      <div className="w-10 h-10 flex items-center justify-center rounded-full border border-yellow-400/40"
        style={{ background: 'rgba(250,204,21,0.1)' }}>
        <i className={`${icon} text-yellow-400 text-xl`} style={{ filter: 'drop-shadow(0 0 6px rgba(250,204,21,0.8))' }}></i>
      </div>
      <span
        className="font-black text-3xl md:text-4xl text-yellow-400"
        style={{ textShadow: '0 0 16px rgba(250,204,21,0.6)', fontFamily: "'Orbitron', sans-serif" }}
      >
        {decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString()}{suffix}
      </span>
      <span className="text-gray-400 text-sm text-center">{label}</span>
    </div>
  );
}

export default function SocialProof() {
  return (
    <section className="bg-[#0d0d18] py-14 md:py-20 border-y border-purple-900/30">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Headline */}
        <div className="text-center mb-10 md:mb-14">
          <p
            className="text-white font-black text-xl md:text-3xl leading-tight"
            style={{ textShadow: '0 0 20px rgba(168,85,247,0.5)' }}
          >
            Join thousands of players discovering
          </p>
          <p
            className="text-yellow-400 font-black text-xl md:text-3xl"
            style={{ textShadow: '0 0 16px rgba(250,204,21,0.5)' }}
          >
            hidden Roblox secrets 🔥
          </p>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {counters.map((c) => (
            <CounterItem key={c.label} {...c} />
          ))}
        </div>

        {/* Scrolling testimonials */}
        <div className="mt-10 md:mt-14 overflow-hidden relative">
          <div className="flex gap-6 animate-marquee whitespace-nowrap">
            {[...Array(2)].map((_, gi) => (
              <div key={gi} className="flex gap-6 shrink-0">
                {[
                  { name: 'xXGamerKid99', text: 'Found a secret room I never knew existed!! 🤯' },
                  { name: 'ProRobloxer', text: 'The glitch guide is INSANE, works perfectly!' },
                  { name: 'SecretHunter', text: 'Best $9 I ever spent on Roblox stuff fr fr' },
                  { name: 'NightOwl_Plays', text: 'My friends can\'t believe I know all these secrets 😂' },
                  { name: 'RobloxQueen', text: 'The map pack has locations nobody talks about!' },
                  { name: 'HiddenWorld', text: 'Membership is worth every penny, new secrets weekly!' },
                ].map((t) => (
                  <div
                    key={t.name}
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-purple-800/40 shrink-0"
                    style={{ background: 'rgba(168,85,247,0.08)' }}
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-purple-700/50 shrink-0">
                      <i className="ri-user-smile-line text-yellow-400 text-sm"></i>
                    </div>
                    <span className="text-yellow-400 font-bold text-sm">{t.name}:</span>
                    <span className="text-gray-300 text-sm">{t.text}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 28s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}
